import React, { Component, useState } from 'react';
import { array, bool, func, object, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { Button, IconAdd, IconClose, ValidationError } from '../../components';
import { composeValidators, maxLength, minLength, numericOnly } from '../../util/validators';
import { useIntl } from 'react-intl';

import css from './FieldTagsInput.module.css';

const FieldTagsInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    id,
    label,
    input,
    meta,
    isRequired,
    placeholder,
    customErrorText,
    hideErrorMessage,
    onUnmount,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const errorText = customErrorText || error;
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  const intl = useIntl();

  const classes = classNames(rootClassName || css.root, className);
  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
    });

  const rawValue = input.value;
  const value = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];
  const onChange = input.onChange;

  const [tagValue, setTagValue] = useState('');
  const [localError, setLocalError] = useState('');

  const validateCode = composeValidators(
    numericOnly(intl.formatMessage({ id: 'CompanyForm.companyCodeDigitsOnly' })),
    minLength(intl.formatMessage({ id: 'General.minLengthValidation' }, { minLength: 5 }), 5),
    maxLength(intl.formatMessage({ id: 'General.maxLengthValidation' }, { maxLength: 10 }), 10)
  );

  const addTag = () => {
    const trimmed = tagValue.trim();
    const errorMsg = validateCode(trimmed);

    if (errorMsg) {
      setLocalError(errorMsg);
      return;
    }

    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setTagValue('');
      setLocalError('');
    }
  };

  const removeTag = tag => {
    onChange(value.filter(t => t !== tag));
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const currentError = tagValue ? validateCode(tagValue.trim()) : '';
  const isAddDisabled = !tagValue.trim() || !!currentError;

  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id}>
          {label}
          {isRequired ? <span className={css.required}>*</span> : null}
        </label>
      ) : null}

      <div className={css.fieldContainer}>
        <input
          id={id}
          type="text"
          value={tagValue}
          placeholder={placeholder}
          onChange={e => {
            setTagValue(e.target.value);
            setLocalError('');
          }}
          onKeyDown={handleKeyDown}
          className={inputClasses}
          {...rest}
        />
        <Button type="button" onClick={addTag} className={css.addButton} disabled={isAddDisabled}>
          <IconAdd />
        </Button>
      </div>

      {(localError || currentError) && (
        <div className={css.localError}>{localError || currentError}</div>
      )}

      {value.length > 0 && (
        <div className={css.tagsList}>
          {value.map(tag => (
            <div key={tag} className={css.tag}>
              {tag}
              <button type="button" className={css.removeButton} onClick={() => removeTag(tag)}>
                <IconClose size="small" />
              </button>
            </div>
          ))}
        </div>
      )}

      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

FieldTagsInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  id: null,
  label: null,
  placeholder: '',
  customErrorText: null,
  hideErrorMessage: false,
  isRequired: false,
  onUnmount: null,
};

FieldTagsInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,
  id: string,
  label: string,
  placeholder: string,
  customErrorText: string,
  hideErrorMessage: bool,
  isRequired: bool,
  onUnmount: func,
  input: shape({
    onChange: func.isRequired,
    value: array,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTagsInput extends Component {
  componentWillUnmount() {
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTagsInputComponent} {...this.props} />;
  }
}

export default FieldTagsInput;
